import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { NetworkStatus } from "apollo-client";
import produce from "immer";
import { remove, some } from "lodash";
import moment from "moment";
import { useInView } from "react-intersection-observer";
import { useInterval } from "@sonder/ui/source/hooks/useInterval";
import {
  Drawer,
  DrawerFunctionParams,
} from "@sonder/ui/source/styled/components/Drawer";
import { ModalHeader } from "@sonder/ui/source/styled/components/Modal";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import {
  ModalClose,
  ModalTitle,
} from "@sonder/ui/source/styled/fragments/Modal";
import { borders, colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { CabinetDrawers, useLayout } from "core/components/LayoutProvider";
import { THIRTY_SECONDS } from "core/constants";
import { usePageVisibility } from "core/hooks/usePageVisibility";
import {
  GetNotificationsResponse,
  GetUserNotification,
  useGetNotifications,
} from "core/queries/GetNotifications";
import {
  updateUserNotificationsOptimisticResponse,
  useUpdateUserNotifications,
} from "core/queries/UpdateUserNotifications";
import { ArchiveAllNotificationsModal } from "users/components/ArchiveAllNotificationsModal";
import {
  EmptyNotificationCardFragment,
  NotificationCard,
  NotificationCardFragment,
} from "users/components/NotificationCard";
import {
  NotificationCardContentLoader,
  StyledNotificationCardContentLoader,
} from "users/components/NotificationCard.loader";

const StyledDrawerHeader = styled(ModalHeader)`
  background: ${colors.white};
  box-sizing: border-box;
  border-bottom: ${borders.standard};
  height: 3.75rem;
  margin: 0;
  padding: 0.5rem 1.71rem;
  position: sticky;
  top: 0;
  z-index: 1;

  ${ModalTitle} {
    font-size: 1.5rem;
    font-weight: bold;
  }

  ${ModalClose} {
    margin: 0;
  }

  ${Link} {
    margin-left: auto;
    margin-right: 1rem;
  }
`;

const StyledEmptyState = styled.p`
  color: ${colors.gray6};
  padding: 0.5rem 1.71rem;
  text-align: center;
`;

const StyledIntersectionObserver = styled.div`
  padding: 1px;
`;

const StyledNotificationList = styled.div`
  ${NotificationCardFragment},
  ${EmptyNotificationCardFragment},
  ${StyledNotificationCardContentLoader} {
    border-bottom: ${borders.standard};
  }
`;

interface NotificationCenterContentsProps extends DrawerFunctionParams {
  isOpen?: boolean;
}

const NotificationCenterContents = ({
  closeDrawer,
  drawerState,
  isOpen,
}: NotificationCenterContentsProps) => {
  const initialValues = {
    cursor: "0",
    limit: 25,
  };
  const { track } = useSegment();
  const { setSidebar } = useLayout();
  const [headerRef, isHeaderInView] = useInView();
  const [footerRef, isFooterInView] = useInView();

  const { isVisible } = usePageVisibility();
  const [isArchiveAllModalOpen, setArchiveAllModalOpen] = useState(false);
  const [modifiedNotifications, setModifiedNotifications] = useState<
    GetUserNotification[]
  >([]);
  const getNotificationsOptions = {
    skip: !isOpen && drawerState === "exited",
    variables: {
      pagination: initialValues,
    },
  };
  const {
    loading,
    fetchMore,
    networkStatus,
    notificationConnections,
    notifications,
    refetch,
  } = useGetNotifications(getNotificationsOptions);
  const { cursor, hasMore } = notificationConnections || {};
  const onFetchComplete = (
    prev: GetNotificationsResponse,
    fetchMoreResult: GetNotificationsResponse,
  ) => {
    if (
      !fetchMoreResult ||
      !fetchMoreResult.notificationConnections.notifications
    )
      return prev;

    const prevNotifications = prev.notificationConnections
      ? prev.notificationConnections.notifications
      : [];
    const newNotifications = fetchMoreResult.notificationConnections
      ? fetchMoreResult.notificationConnections.notifications
      : [];
    return {
      ...prev,
      ...fetchMoreResult,
      notificationConnections: {
        ...prev.notificationConnections,
        ...fetchMoreResult.notificationConnections,
        notifications: [...(prevNotifications || []), ...newNotifications],
      },
    };
  };
  const onFetchMore = () => {
    const variables = {
      pagination: {
        cursor,
        limit: initialValues.limit,
      },
    };
    fetchMore({
      updateQuery: (prev, { fetchMoreResult }) =>
        fetchMoreResult
          ? onFetchComplete(
              prev as GetNotificationsResponse,
              fetchMoreResult as GetNotificationsResponse,
            )
          : prev,
      variables,
    });
  };
  const [onUpdateUserNotifications] = useUpdateUserNotifications({
    awaitRefetchQueries: true,
    refetchQueries: ["GetNotifications", "GetNotificationsTotal"],
  });
  const onArchive = (notification: GetUserNotification) => {
    const archive = !notification.archived;
    const modifiedNotification = produce(notification, draft => {
      draft.archived = archive;
      return draft;
    });
    setModifiedNotifications(
      modifiedNotifications
        .filter(
          archivedNotification =>
            archivedNotification.id !== modifiedNotification.id,
        )
        .concat([modifiedNotification]),
    );
    onUpdateUserNotifications({
      variables: {
        notificationIds: [notification.id],
        archive,
      },
      optimisticResponse: updateUserNotificationsOptimisticResponse({
        id: notification.id,
        archived: archive,
        success: true,
      }),
    });
    track({
      eventName: "notification_center",
      action: "click",
      element: "archive_notification",
      props: { nextValue: notification.id },
    });
  };
  useInterval(() => {
    if (isVisible && isHeaderInView && !loading)
      refetch({
        pagination: initialValues,
      });
  }, THIRTY_SECONDS);
  useEffect(() => {
    if (hasMore && isFooterInView && !loading) onFetchMore();
  }, [hasMore, isFooterInView, loading, onFetchMore]);
  const combinedNotifs = useMemo(
    () =>
      notifications
        .filter(
          notification =>
            !some(
              modifiedNotifications,
              archivedNotification =>
                archivedNotification.id === notification.id,
            ),
        )
        .concat(modifiedNotifications)
        .sort(
          (a, b) =>
            moment(b.activityTime).valueOf() - moment(a.activityTime).valueOf(),
        ),
    [notifications, modifiedNotifications],
  );
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
  const isNotificationsEmpty = combinedNotifs && combinedNotifs.length === 0;
  const shouldShowLoader = isFetchingMore || (isNotificationsEmpty && loading);
  const shouldShowArchiveAll = !isNotificationsEmpty;
  return (
    <>
      <StyledDrawerHeader title="Notifications" onClose={closeDrawer}>
        {shouldShowArchiveAll && (
          <Link
            onClick={() => {
              setArchiveAllModalOpen(true);
            }}
          >
            Archive All
          </Link>
        )}
      </StyledDrawerHeader>
      <StyledNotificationList>
        {<StyledIntersectionObserver ref={headerRef} />}
        {!loading && isNotificationsEmpty && (
          <StyledEmptyState>
            🍍 You currently have no unread Notifications, <b>go you</b>!
          </StyledEmptyState>
        )}
        {combinedNotifs.map((notification: GetUserNotification) => (
          <NotificationCard
            key={notification.id}
            notification={notification}
            onArchive={() =>
              requestAnimationFrame(() => onArchive(notification))
            }
            onClick={() => {
              if (window.innerWidth <= 800 && closeDrawer) {
                closeDrawer();
                setSidebar("collapsed");
              }
            }}
          />
        ))}
        {shouldShowLoader && (
          <>
            <NotificationCardContentLoader />
            <NotificationCardContentLoader />
            <NotificationCardContentLoader />
          </>
        )}
        {hasMore && <StyledIntersectionObserver ref={footerRef} />}
      </StyledNotificationList>
      <ArchiveAllNotificationsModal
        isOpen={isArchiveAllModalOpen}
        onClose={() => setArchiveAllModalOpen(false)}
      />
    </>
  );
};

export const NotificationCenterDrawer = () => {
  const { track } = useSegment();
  const { openCabinetDrawers, setOpenCabinetDrawers } = useLayout();
  const isOpen = openCabinetDrawers.includes(CabinetDrawers.NotificationCenter);
  return (
    <Drawer
      isOpen={isOpen}
      onClose={() => {
        track({
          eventName: "notification_center",
          action: "click",
          element: "close",
        });
        setOpenCabinetDrawers(
          remove(openCabinetDrawers, CabinetDrawers.NotificationCenter),
        );
      }}
    >
      {({ closeDrawer, drawerState }) => (
        <NotificationCenterContents
          closeDrawer={closeDrawer}
          drawerState={drawerState}
          isOpen={isOpen}
        />
      )}
    </Drawer>
  );
};
